import { ActivityPurchaseModel } from 'api-hooks/order/activity';
import { PurchaseTypeEnum } from 'api-hooks/order/common/model';
import { ActivitySaleModel } from 'api-hooks/sale/activity';
import { PurchaseRelatedModel } from 'api-hooks/sale/common';
import { generateId } from 'common/utils/string';
import Separator from 'components/common/separator';
import { Input } from 'components/elements';
import { useFormState } from 'components/elements/form';
import Text from 'components/elements/text';
import { HalfContainer, SeparatorVertical } from 'components/styles';
import AgentSelectInput from 'legacy-modules/master/agent/components/select-input';
import PaymentChannelSelectInput from 'legacy-modules/master/payment-channel/components/select-input';
import AgentSupendToast from 'legacy-modules/order/component/agent-suspend-toast';
import ActivitySaleSelectInput from 'legacy-modules/sales/activity/components/select-input';
import PurchaseRelatedTable from 'legacy-modules/sales/component/purchase-related-table';
import TransactionAt from 'legacy-modules/sales/component/transaction-at';
import { guestNameTransformer } from 'legacy-modules/sales/ticket-sale/hooks/use-get-default-values';
import {
  CardSection,
  FullContainer,
  SectionContainer,
} from 'legacy-modules/styles';
import TourListSelectInput from 'legacy-modules/tour/tour-list/components/select-input';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import {
  ActivityPurchaseFormType,
  ActivityPurchasesItemFormType,
} from './form-type';

interface Props {
  activityPurchase?: ActivityPurchaseModel;
}

interface PurchaseDetailCardProps extends Props {
  setPurchases: React.Dispatch<React.SetStateAction<PurchaseRelatedModel[]>>;
}

function PurchasesDetailCardComponent(props: PurchaseDetailCardProps) {
  const { t } = useTranslation();
  const { setValue, control, trigger, getValues, reset } =
    useFormContext<ActivityPurchaseFormType>();
  const [type, activitySale, total, remainingTransactionQuota] = useWatch({
    control,
    name: ['type', 'activitySale', 'total', 'remainingTransactionQuota'],
  });
  const { editable } = useFormState();

  const isExceedingQuota =
    remainingTransactionQuota && total > remainingTransactionQuota;

  React.useEffect(() => {
    setValue('isExceedingQuota', isExceedingQuota || false);
  }, [setValue, isExceedingQuota]);

  const invoiceId = getValues('invoiceId');
  React.useEffect(() => {
    if (!invoiceId || invoiceId === '') {
      reset();
      props.setPurchases([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId, reset]);

  const onAfterDetailChangeInvoice = React.useCallback(
    (value: ActivitySaleModel) => {
      if (editable) {
        if (!props.activityPurchase) {
          props.setPurchases(value.purchases);
        }

        const activityPurchases: ActivityPurchasesItemFormType[] =
          value.activitySales.map((as) => {
            const activityPurchaseAddOns: ActivityPurchasesItemFormType['activityPurchaseAddOns'] =
              as.activitySaleAddOns.map((addons) => {
                const guest = addons.guest;
                return {
                  customId: generateId(),
                  id: addons.id,
                  guestId: guest.id,
                  guestName: guestNameTransformer(guest),
                  description: addons.description || '',
                  price: addons.price,
                };
              });

            const activityPurchaseGuests: ActivityPurchasesItemFormType['activityPurchaseGuests'] =
              as.activitySaleGuests.map((guest) => {
                const _guest = guest.guest;
                return {
                  customId: generateId(),
                  id: guest.id,
                  guestId: _guest.id,
                  guestName: guestNameTransformer(_guest),
                  activityPurchaseGuestDetails:
                    guest.activitySaleGuestDetails.map((detail) => {
                      const activity = detail.activity;
                      return {
                        customId: generateId(),
                        id: detail.id,
                        date: detail.date,
                        activityId: activity.id,
                        activityName: activity.name,
                        type: activity.type,
                        price: detail.price,
                        qty: detail.qty,
                        totalPrice: detail.totalPrice,
                        description: detail.description || '',
                      };
                    }),
                };
              });

            return {
              customId: generateId(),
              id: as.id,
              isGroupedPrice: as.isGroupedPrice,
              activityPurchaseGuests,
              activityPurchaseAddOns,
            };
          });

        setValue('activityPurchases', activityPurchases);
        setValue('discount', value.discount);
        setValue('otherCost', value.otherCost);

        setValue('saleTotal', value.total);
        setValue('invoiceId', value.id);
        setValue('invoiceRefId', value.refId);
        trigger(['activityPurchases', 'discount', 'otherCost']);
      }
    },
    [editable, props, setValue, trigger],
  );

  const onEnumAfterChange = React.useCallback(() => {
    props.setPurchases([]);
    setValue('invoiceRefId', '', {
      shouldTouch: true,
      shouldValidate: true,
    });
    setValue('tourRefId', '', {
      shouldTouch: true,
      shouldValidate: true,
    });
  }, [props, setValue]);

  const agentId = getValues('agentId');
  React.useEffect(() => {
    if (!agentId || agentId === '') {
      setValue('agentStatus', '');
      setValue('remainingTransactionQuota', null);
      setValue('isExceedingQuota', false);
    }
  }, [agentId, setValue]);

  const onAfterAgentDetailChange = React.useCallback(
    (result) => {
      if (editable) {
        const remainingTransactionQuota =
          result.system.remainingTransactionQuota;
        const isExceedingQuota =
          result.system.isTransactionLimited &&
          total > remainingTransactionQuota;

        setValue('isExceedingQuota', isExceedingQuota);
        setValue(
          'remainingTransactionQuota',
          remainingTransactionQuota || null,
        );
        setValue('agentStatus', result.status);
      }
    },
    [editable, setValue, total],
  );

  const disabled = !!props.activityPurchase || !!activitySale;
  return (
    <CardSection style={{ paddingBottom: 0 }}>
      <FullContainer>
        <Text variant="HeadingXSmall">{t('ticketOrders:orders-details')}</Text>
        <SeparatorVertical $margin={24} />
        <SectionContainer>
          {props.activityPurchase && (
            <HalfContainer>
              <Input
                type="text"
                name="number"
                label={t('common:order_number')}
                placeholder={t('common:order_number')}
                disabled
              />
            </HalfContainer>
          )}
          <HalfContainer
            $style={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
            }}
          >
            <TransactionAt
              name="transactionAt"
              clearable={false}
              hasTime
              required
            />
          </HalfContainer>
        </SectionContainer>
        <SectionContainer>
          <HalfContainer>
            <Input
              type="select"
              label={t('common:type')}
              placeholder={t('common:type')}
              name="type"
              required
              onAfterChange={onEnumAfterChange}
              clearable={false}
              disabled={disabled}
              options={[
                { id: PurchaseTypeEnum.Sales, label: t('common:sales') },
                { id: PurchaseTypeEnum.Tour, label: t('common:tour') },
              ]}
            />
          </HalfContainer>
          <HalfContainer>
            {type === PurchaseTypeEnum.Sales ? (
              <ActivitySaleSelectInput
                label={t('common:invoice_number')}
                placeholder={t('common:choose_invoice')}
                onAfterDetailChange={onAfterDetailChangeInvoice}
                name="invoiceId"
                disabled={disabled}
                type="invoice"
                required
              />
            ) : (
              type === PurchaseTypeEnum.Tour && (
                <TourListSelectInput
                  name="tourId"
                  disabled={!!props.activityPurchase}
                  required
                  onAfterDetailChange={(value) => {
                    setValue('tourRefId', value.refId);
                  }}
                />
              )
            )}
          </HalfContainer>
        </SectionContainer>
        <SectionContainer>
          <HalfContainer>
            <AgentSelectInput
              name="agentId"
              remainingTransactionQuota={remainingTransactionQuota}
              onAfterDetailChange={onAfterAgentDetailChange}
              required
            />
          </HalfContainer>
          <HalfContainer>
            <PaymentChannelSelectInput name="paymentChannelId" />
          </HalfContainer>
        </SectionContainer>
        <SeparatorVertical $margin={8} />
      </FullContainer>
      <AgentSupendToast />
    </CardSection>
  );
}

export default function PurchasesDetailCard(props: Props) {
  const { t } = useTranslation();
  const [purchases, setPurchases] = React.useState<PurchaseRelatedModel[]>([]);
  const { control } = useFormContext<ActivityPurchaseFormType>();
  const [type] = useWatch({
    control,
    name: ['type'],
  });

  return (
    <>
      <PurchasesDetailCardComponent {...props} setPurchases={setPurchases} />
      {type === PurchaseTypeEnum.Sales &&
        !props.activityPurchase &&
        !!purchases.length && (
          <>
            <SeparatorVertical $margin={24} />
            <Text variant="HeadingXSmall">{t('common:related_purchases')}</Text>
            <Separator gap={8} />
            <PurchaseRelatedTable
              data={purchases}
              navigation="ActivityPurchasesView"
            />
          </>
        )}
    </>
  );
}
